import { Helmet } from "react-helmet-async";
import { useEffect, useState } from "react";
import { client, NAP, BOOKING_URL } from "./config";

// Cache the settings once per app load so canonical/base URL is consistent.
let cached = null;
export function useSiteBase() {
  const [base, setBase] = useState(cached?.site_base_url || "https://westbridgeinnandsuites.com");
  const [settings, setSettings] = useState(cached);
  useEffect(() => {
    if (cached) return;
    client.get("/settings").then((r) => {
      cached = r.data;
      setSettings(cached);
      if (cached.site_base_url) setBase(cached.site_base_url.replace(/\/$/, ""));
    }).catch(() => {});
  }, []);
  return { base, settings };
}

export default function SEOHead({
  title, description, canonicalPath, image,
  ogType = "website", schema, noindex = false,
}) {
  const { base, settings } = useSiteBase();
  const fullTitle = title ? `${title} | ${NAP.name}` : `${NAP.name} — Hotel in Clinton, MO`;
  const desc = description || `${NAP.name} in ${NAP.city}, ${NAP.state}. Renovated rooms, 24-hour desk, free hot breakfast, boat & truck parking. ${NAP.slogan}`;
  const path = canonicalPath || (typeof window !== "undefined" ? window.location.pathname : "/");
  const canonical = `${base}${path === "/" ? "" : path}`;
  const ogImage = image ? (image.startsWith("http") ? image : `${base}${image}`) : `${base}/images/exterior-1.jpg`;
  const ga4 = settings?.ga4_id;
  const gsv = settings?.google_site_verification;
  const bsv = settings?.bing_site_verification;

  const schemas = Array.isArray(schema) ? schema : (schema ? [schema] : []);

  return (
    <Helmet>
      <title>{fullTitle}</title>
      <meta name="description" content={desc} />
      <link rel="canonical" href={canonical} />
      {noindex && <meta name="robots" content="noindex,nofollow" />}

      {/* OG */}
      <meta property="og:site_name" content={NAP.name} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={desc} />
      <meta property="og:type" content={ogType} />
      <meta property="og:url" content={canonical} />
      <meta property="og:image" content={ogImage} />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={desc} />
      <meta name="twitter:image" content={ogImage} />

      {/* Site verification (only when set in settings) */}
      {gsv && <meta name="google-site-verification" content={gsv} />}
      {bsv && <meta name="msvalidate.01" content={bsv} />}

      {/* Per-page JSON-LD */}
      {schemas.map((s, i) => (
        <script key={i} type="application/ld+json">{JSON.stringify(s)}</script>
      ))}

      {/* GA4 — only when a valid G-XXXXXXXXXX is saved */}
      {ga4 && /^G-[A-Z0-9]{4,20}$/.test(ga4) && (
        <script async src={`https://www.googletagmanager.com/gtag/js?id=${ga4}`} />
      )}
      {ga4 && /^G-[A-Z0-9]{4,20}$/.test(ga4) && (
        <script>{`window.dataLayer=window.dataLayer||[];function gtag(){dataLayer.push(arguments);}gtag('js', new Date());gtag('config','${ga4}');`}</script>
      )}
    </Helmet>
  );
}

// -----------------------------------------------------------------------
// Schema builders
// -----------------------------------------------------------------------
export function hotelSchema(base) {
  const origin = (base || "https://westbridgeinnandsuites.com").replace(/\/$/, "");
  return {
    "@context": "https://schema.org",
    "@type": ["Hotel", "LodgingBusiness"],
    "@id": `${origin}/#hotel`,
    "name": NAP.name,
    "alternateName": [
      "Westbridge Inn and Suites",
      "Westbridge Inn & Suites Clinton MO",
      "Westbridge Clinton",
    ],
    "slogan": NAP.slogan,
    "description": "Independent 32-room hotel in Clinton, MO — renovated rooms, 24-hour front desk, free hot breakfast, seasonal outdoor pool, pet friendly, boat and truck parking with boat charging access.",
    "url": origin,
    "telephone": NAP.phoneTel,
    "email": NAP.email,
    "image": `${origin}/images/exterior-hero.jpg`,
    "logo": `${origin}/images/logo-horizontal.png`,
    "priceRange": "$$",
    "numberOfRooms": 32,
    "petsAllowed": true,
    "checkinTime": "15:00",
    "checkoutTime": "11:00",
    "address": {
      "@type": "PostalAddress",
      "streetAddress": NAP.addressLine1,
      "addressLocality": NAP.city,
      "addressRegion": NAP.state,
      "postalCode": NAP.postal,
      "addressCountry": "US",
    },
    "geo": {
      "@type": "GeoCoordinates",
      "latitude": 38.3689413,
      "longitude": -93.7512258,
    },
    "amenityFeature": [
      { "@type": "LocationFeatureSpecification", "name": "24-hour front desk", "value": true },
      { "@type": "LocationFeatureSpecification", "name": "Free deluxe hot breakfast", "value": true },
      { "@type": "LocationFeatureSpecification", "name": "Free Wi-Fi", "value": true },
      { "@type": "LocationFeatureSpecification", "name": "Seasonal outdoor pool", "value": true },
      { "@type": "LocationFeatureSpecification", "name": "Free parking", "value": true },
      { "@type": "LocationFeatureSpecification", "name": "Pet friendly (2 pets per room)", "value": true },
      { "@type": "LocationFeatureSpecification", "name": "Truck & bus parking", "value": true },
      { "@type": "LocationFeatureSpecification", "name": "Boat & trailer parking", "value": true },
      { "@type": "LocationFeatureSpecification", "name": "Boat charging access", "value": true },
      { "@type": "LocationFeatureSpecification", "name": "Kids under 18 stay free", "value": true },
      { "@type": "LocationFeatureSpecification", "name": "Smoke-free property", "value": true },
    ],
    "potentialAction": {
      "@type": "ReserveAction",
      "target": {
        "@type": "EntryPoint",
        "urlTemplate": BOOKING_URL,
        "inLanguage": "en-US",
        "actionPlatform": ["http://schema.org/DesktopWebPlatform", "http://schema.org/MobileWebPlatform"],
      },
      "result": { "@type": "LodgingReservation", "name": "Reserve a room at Westbridge Inn & Suites" },
    },
  };
}

export function breadcrumbSchema(base, items) {
  const origin = (base || "").replace(/\/$/, "");
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      { "@type": "ListItem", "position": 1, "name": "Home", "item": origin || "/" },
      ...items.map((it, i) => ({
        "@type": "ListItem",
        "position": i + 2,
        "name": it.label,
        "item": it.to ? `${origin}${it.to}` : undefined,
      })),
    ],
  };
}

export function faqPageSchema(faqs) {
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqs.map((f) => ({
      "@type": "Question",
      "name": f.question,
      "acceptedAnswer": { "@type": "Answer", "text": f.answer },
    })),
  };
}

export function articleSchema(base, post) {
  const origin = (base || "").replace(/\/$/, "");
  return {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    "mainEntityOfPage": `${origin}/blog/${post.slug}`,
    "headline": post.title,
    "description": post.excerpt,
    "image": post.hero.startsWith("http") ? post.hero : `${origin}${post.hero}`,
    "datePublished": post.published_at,
    "dateModified": post.published_at,
    "author": { "@type": "Organization", "name": post.author, "url": origin },
    "publisher": {
      "@type": "Organization",
      "name": NAP.name,
      "logo": { "@type": "ImageObject", "url": `${origin}/images/logo-horizontal.png` },
    },
    "articleSection": post.category,
    "keywords": (post.tags || []).join(", "),
  };
}

export function roomsCollectionSchema(base, rooms) {
  const origin = (base || "").replace(/\/$/, "");
  return {
    "@context": "https://schema.org",
    "@type": "ItemList",
    "itemListElement": rooms.map((r, i) => ({
      "@type": "ListItem",
      "position": i + 1,
      "item": {
        "@type": "HotelRoom",
        "name": r.name,
        "description": r.description,
        "url": `${origin}/rooms#${r.slug}`,
        "image": r.hero_photo.startsWith("http") ? r.hero_photo : `${origin}${r.hero_photo}`,
        "occupancy": { "@type": "QuantitativeValue", "maxValue": r.max_guests },
        "bed": r.bed_config,
        "offers": {
          "@type": "Offer",
          "price": r.price_from,
          "priceCurrency": "USD",
          "url": BOOKING_URL,
          "availability": "https://schema.org/InStock",
        },
      },
    })),
  };
}

export function imageGallerySchema(base, images) {
  const origin = (base || "").replace(/\/$/, "");
  return {
    "@context": "https://schema.org",
    "@type": "ImageGallery",
    "name": `${NAP.name} Photo Gallery`,
    "image": images.slice(0, 20).map((img) => (img.startsWith("http") ? img : `${origin}${img}`)),
  };
}
